import { useEffect, useRef, useState } from "react";
import { Vergleich } from "@/components/Vergleich";
import { Container } from "@/components/ui/container";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { useSeo, generateBreadcrumbSchema } from "@/hooks/useSeo";
import type { Zelt } from "@/data/zelte";

interface VergleichSeiteProps {
  vergleichIds: string[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onReplace: (ids: string[]) => void;
  onNavigate: (path: string) => void;
}

export function VergleichSeite({ vergleichIds, onRemove, onClear, onReplace, onNavigate }: VergleichSeiteProps) {
  const [zelte, setZelte] = useState<Zelt[]>([]);
  const [loading, setLoading] = useState(true);
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("ids");
    if (fromUrl) {
      const ids = fromUrl.split(",").map(id => id.trim()).filter(Boolean).slice(0, 4);
      if (ids.length) onReplace(ids);
    }
  }, [onReplace]);

  useEffect(() => {
    if (vergleichIds.length === 0) {
      if (window.location.search.includes("ids=")) {
        window.history.replaceState({}, "", "/vergleich");
      }
      setZelte([]);
      setLoading(false);
      return;
    }

    const next = `/vergleich?ids=${vergleichIds.join(",")}`;
    if (window.location.pathname + window.location.search !== next) {
      window.history.replaceState({}, "", next);
    }
  }, [vergleichIds]);

  useEffect(() => {
    if (vergleichIds.length === 0) {
      setZelte([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch("/api/vergleich", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: vergleichIds }),
    })
      .then(res => res.json())
      .then(data => {
        setZelte(data.zelte || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vergleichIds]);

  useSeo({
    title: `Zelt-Vergleich${zelte.length > 0 ? ` - ${zelte.length} Zelte` : ""} | zelt.kaufen`,
    description: zelte.length > 0
      ? `Vergleichen Sie ${zelte.map(z => z.name).join(", ")} direkt nebeneinander. Preis, Features, Bewertungen im Detail.`
      : "Vergleichen Sie bis zu 4 Zelte nebeneinander und finden Sie das beste Modell für Ihre Bedürfnisse.",
    keywords: "Zeltvergleich, Zelte vergleichen, Camping Vergleich",
    type: "website",
    schema: generateBreadcrumbSchema([
      { name: "Startseite", url: window.location.origin },
      { name: "Vergleich", url: window.location.href },
    ]),
  });

  if (loading) {
    return (
      <Container className="py-8">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-32 mb-4" />
          <div className="h-10 bg-muted rounded w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-96 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <Container className="pt-8">
        <Breadcrumb items={[{ label: "Vergleich" }]} className="mb-4" />
      </Container>
      <Vergleich zelte={zelte} onRemove={onRemove} onNavigate={onNavigate} onClear={onClear} />
    </>
  );
}
